import React, { useMemo } from 'react'
import type { CategoryInterface, Maybe } from '@pylot-data/pylotschema'
import { CmsPage, ProductInterface } from '@pylot-data/pylotschema'
import { useRouter } from 'next/router'
import { Product } from './Product'
import { Category } from './Category'
import { CMSPage } from './CMSPage'
import { Breadcrumbs } from './Breadcrumbs'
import { SRP } from './Srp'
import { Rewards } from './Rewards'
import { FutureOfHookah } from './FutureOfHookah'
import Head from 'next/head'
import { SEO_ENUMS } from './enums'
import { IS_YOUTUBE_SEO_ENABLED } from '@components/StoreConfiguration/StoreConfiguration'
import { YoutubeVideoSchema } from '@components/seo/src/YoutubeVideo/YoutubeVideoSchema'
import {
    Blog,
    BlogCategory,
    BlogIndex,
    CommonBlog
} from '@components/seo/src/Blog'
import { BlogInterface } from '@components/seo/src/Blog/Blog'
import { BlogCategoryInterface } from '@components/seo/src/Blog/BlogCatergory'
import { CommonBlogInterface } from '@components/seo/src/Blog/CommonBlog'
import { BlogLandingInterface } from '@components/seo/src/Blog/BlogIndex'
import { BlogBreadCrumbsDetails } from '@components/seo/src/Blog/BlogSchema'
import { StoreLocator } from '@components/seo/src/StoreLocator'

const corePages = [
    SEO_ENUMS.PLP,
    SEO_ENUMS.PDP,
    SEO_ENUMS.CMS,
    SEO_ENUMS.HOME,
    SEO_ENUMS.BHome,
    SEO_ENUMS.BLOG,
    SEO_ENUMS.CBS,
    SEO_ENUMS.BCP
]

type Page = SEO_ENUMS

export type SEOProps = {
    product?: Maybe<ProductInterface>
    cmsPage?: Maybe<CmsPage>
    category?: Maybe<CategoryInterface>
    blog?: Maybe<BlogInterface>
    blogLanding?: Maybe<BlogLandingInterface>
    blogCategory?: Maybe<BlogCategoryInterface>
    blogBreadCrumbs?: BlogBreadCrumbsDetails
    commonBlog?: Maybe<CommonBlogInterface>
    titleOverride?: string
    descriptionOverride?: string
    keywordOverride?: string
    page?: Page
    products?: Maybe<Array<ProductInterface>>
}

export const SEO = ({
    product,
    cmsPage,
    category,
    blog,
    blogLanding,
    blogCategory,
    commonBlog,
    titleOverride,
    descriptionOverride,
    keywordOverride,
    products,
    blogBreadCrumbs,
    page = SEO_ENUMS.OTHER
}: SEOProps): JSX.Element => {
    const { asPath } = useRouter()
    const pathname = String(asPath).replace(/^\//, '')

    const breadcrumbs = useMemo(
        () =>
            corePages.indexOf(page) >= 0 && (
                <Breadcrumbs
                    blogBreadCrumbs={blogBreadCrumbs}
                    product={product}
                    cmsPage={cmsPage}
                    category={category}
                    titleOverride={titleOverride}
                    pathname={pathname}
                />
            ),
        [pathname]
    )

    const iframeVideo = useMemo(
        () => <YoutubeVideoSchema pathname={pathname} />,
        [pathname]
    )

    const productSeo = useMemo(
        () =>
            page == 'pdp' &&
            product && (
                <Product
                    product={product}
                    titleOverride={titleOverride}
                    descriptionOverride={descriptionOverride}
                    pathname={pathname}
                />
            ),
        [descriptionOverride, pathname, product, titleOverride]
    )

    const cmsPageSeo = useMemo(
        () =>
            (page === 'cms' || page === 'home') &&
            cmsPage && (
                <CMSPage
                    page={page}
                    cmsPage={cmsPage}
                    titleOverride={titleOverride}
                    descriptionOverride={descriptionOverride}
                    pathname={pathname}
                    asPath={asPath}
                />
            ),
        [cmsPage, descriptionOverride, pathname, titleOverride]
    )

    const categorySeo = useMemo(
        () =>
            page === 'plp' &&
            category && (
                <Category
                    category={category}
                    titleOverride={titleOverride}
                    descriptionOverride={descriptionOverride}
                    pathname={pathname}
                    products={products}
                />
            ),
        [category, descriptionOverride, pathname, titleOverride, products]
    )

    const blogSeo = useMemo(
        () =>
            page === 'blog' &&
            blog && (
                <Blog
                    blog={blog}
                    titleOverride={titleOverride}
                    descriptionOverride={descriptionOverride}
                    pathname={pathname}
                    asPath={asPath}
                />
            ),
        [blog, descriptionOverride, pathname, titleOverride]
    )

    const blogCategorySeo = useMemo(
        () =>
            page === 'bcp' &&
            blogCategory && (
                <BlogCategory
                    blog={blogCategory}
                    titleOverride={titleOverride}
                    descriptionOverride={descriptionOverride}
                    pathname={pathname}
                />
            ),
        [blogCategory, descriptionOverride, pathname, titleOverride]
    )

    const commonBlogSeo = useMemo(
        () =>
            page === 'cbs' &&
            commonBlog && (
                <CommonBlog
                    blog={commonBlog}
                    titleOverride={titleOverride}
                    descriptionOverride={descriptionOverride}
                    pathname={pathname}
                />
            ),
        [commonBlog, descriptionOverride, pathname, titleOverride]
    )

    const blogHomeSeo = useMemo(
        () =>
            page === 'bh' &&
            blogLanding && (
                <BlogIndex
                    blog={blogLanding}
                    titleOverride={titleOverride}
                    descriptionOverride={descriptionOverride}
                    pathname={pathname}
                />
            ),
        [blogLanding, descriptionOverride, pathname, titleOverride]
    )

    const srpSeo = useMemo(
        () => page === 'srp' && <SRP titleOverride={titleOverride} />,
        [titleOverride]
    )

    const RewardsSeo = useMemo(
        () => page === 'rewards' && <Rewards pathname={pathname} />,
        [pathname]
    )

    const FutureOfHookahSeo = useMemo(
        () => page === 'foh' && <FutureOfHookah pathname={pathname} />,
        [pathname]
    )

    const StoreLocatorSeo = useMemo(
        () =>
            page === 'storelocator' && (
                <StoreLocator
                    keywordOverride={keywordOverride}
                    descriptionOverride={descriptionOverride}
                    titleOverride={titleOverride}
                />
            ),
        [titleOverride, descriptionOverride, keywordOverride]
    )

    return (
        <>
            {breadcrumbs}
            {productSeo}
            {cmsPageSeo}
            {categorySeo}
            {blogSeo}
            {srpSeo}
            {blogHomeSeo}
            {blogCategorySeo}
            {commonBlogSeo}
            {RewardsSeo}
            {FutureOfHookahSeo}
            {StoreLocatorSeo}
            {page !== 'srp' && (
                <Head>
                    <meta name="robots" content="index,follow" />
                </Head>
            )}
            {page === 'cbs' && (
                <Head>
                    <meta name="robots" content="noindex, nofollow" />
                </Head>
            )}

            {IS_YOUTUBE_SEO_ENABLED && iframeVideo}
        </>
    )
}
